import { NavLink, useNavigate } from 'react-router-dom';
import {
	BarChart3,
	Boxes,
	Home,
	LayoutGrid,
	LogOut,
	Megaphone,
	Pause,
	Settings as SettingsIcon,
	ShoppingBag,
	Sparkles
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useChannels } from '@/hooks/useChannels';
import { useOrders } from '@/hooks/useOrders';
import { useLocalAds } from '@/hooks/useInsights';
import { bucketFor } from '@/components/orders/StatusPill';
import { StoreSwitcher } from './StoreSwitcher';
import unifyLogo from '@/assets/unify-logo.png';

/**
 * Order matches the Lovable prototype. Customers is reachable via direct URL
 * (`/customers`) but isn't in the main nav — surfaced inside the Results page
 * instead.
 *
 * Each entry's affordances (badge / "+ Learn more" pill) are driven by data
 * the sidebar fetches itself, not a shared lifecycle state. The Orders pill
 * shows when the shop is freshly onboarded with no live channels and no
 * orders yet — i.e., they haven't started selling yet.
 */
export const mainNav = [
	{ to: '/', label: 'Overview', icon: Home, end: true },
	{ to: '/orders', label: 'Orders', icon: ShoppingBag, key: 'orders' },
	{ to: '/ads', label: 'Ads', icon: Megaphone, key: 'ads' },
	{ to: '/channels', label: 'Channels', icon: LayoutGrid },
	{ to: '/inventory', label: 'Inventory', icon: Boxes },
	{ to: '/results', label: 'Results', icon: BarChart3 }
];

export function AppSidebar({ onOpenChat }) {
	const navigate = useNavigate();
	const { shop, shops } = useShop();
	const { shopPath } = useShopPath();
	const { channels, isLoading: channelsLoading } = useChannels(shop?.id);
	const { orders, isLoading: ordersLoading } = useOrders(shop?.id);

	// useChannels / useOrders coerce `data || []`, so during the initial
	// SWR fetch the values arrive here as empty arrays. Without a "ready"
	// gate, every "inactive" affordance would briefly fire on each shop
	// load. Each pill is gated on only the hooks it actually reads — the
	// Ads pill doesn't read orders, so it shouldn't wait for them.
	const channelsReady = Boolean(shop?.id) && !channelsLoading;
	const ordersReady = Boolean(shop?.id) && !ordersLoading;

	// Orders pill is "+ LEARN MORE" iff neither Orders-driving channel
	// (Reservations or Click & Collect) is enabled AND the shop has no
	// orders yet. Listings-only shops, which are the common case for new
	// retailers, should still see the nudge — gating on "no channel at all"
	// would silently swallow it (NEA-5849).
	const noOrdersChannelEnabled = !Array.isArray(channels)
		? false
		: !channels.some(
				(c) => (c.type === 'product-reservations' || c.type === 'local-checkout') && (c.enabled === true || c.status === 'enabled')
			);
	const noOrders = !Array.isArray(orders) || orders.length === 0;
	const ordersInactive = channelsReady && ordersReady && noOrdersChannelEnabled && noOrders;
	// Mirror the Orders page's "Action needed" section (pages/orders/index.jsx
	// — Pending + Confirmed buckets) so the count surfaced in the sidebar is
	// exactly what the user lands on when they click through (NEA-5830). The
	// previous filter (`status === 'awaiting'`) never matched any real status.
	const actionNeededCount = Array.isArray(orders)
		? orders.filter((o) => {
				const bucket = bucketFor(o.status);
				return bucket === 'Pending' || bucket === 'Confirmed';
			}).length
		: 0;
	const ordersBadge = !ordersInactive && actionNeededCount > 0 ? actionNeededCount : null;

	// Ads pill has three states driven by both the channel status and the
	// current plan budget:
	//   - channel disabled / missing → "+ Learn more"
	//   - channel enabled, budget > 0 → live (emerald dot)
	//   - channel enabled, budget == 0 → paused (amber dot)
	// We share the same `useLocalAds` SWR cache the Ads page populates so this
	// doesn't cost an extra fetch on most navigations.
	const { data: adsData } = useLocalAds(shop?.id);
	const localAdsChannel = Array.isArray(channels) ? channels.find((c) => c.type === 'local-ads') : null;
	const channelEnabled = localAdsChannel?.status === 'enabled';
	const adsBudgetDaily = adsData?.budget?.daily ?? null;
	const adsLive = channelEnabled && adsBudgetDaily > 0;
	const adsPaused = channelEnabled && adsBudgetDaily === 0;
	// Gated on channels only — Ads activation doesn't read orders, so
	// coupling it to ordersLoading would needlessly delay this nudge.
	const adsInactive = channelsReady && !channelEnabled;

	const initials = (shop?.name ?? 'Your shop')
		.split(' ')
		.map((p) => p[0])
		.slice(0, 2)
		.join('')
		.toUpperCase();
	const displayName = shop?.name ?? 'Your shop';
	const displayLocation = shop?.city ?? null;

	const onSignOut = () => navigate('/logout');

	return (
		<aside className="flex h-screen w-[248px] shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground">
			{shops.length > 1 ? (
				<StoreSwitcher />
			) : (
				<NavLink
					to={shopPath('/')}
					end
					className="mx-4 mb-6 mt-7 flex items-center gap-3 rounded-2xl border border-sidebar-border bg-white px-3 py-2.5 transition-colors hover:bg-accent"
				>
					<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
						{initials}
					</div>
					<div className="min-w-0">
						<div className="truncate text-sm font-semibold text-foreground">{displayName}</div>
						{displayLocation ? <div className="text-[11px] text-muted-foreground">{displayLocation}</div> : null}
					</div>
				</NavLink>
			)}

			{onOpenChat ? (
				<button
					type="button"
					onClick={onOpenChat}
					data-tour="ai-chat"
					className="group relative mx-4 -mt-2 mb-4 flex w-[calc(100%-2rem)] items-center gap-3 rounded-xl bg-white px-3.5 py-2.5 text-sm font-medium text-foreground shadow-unify transition-all duration-300 hover:-translate-y-0.5 hover:shadow-unify-strong active:translate-y-0"
				>
					<span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md bg-gradient-unify shadow-sm">
						<Sparkles className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
					</span>
					<span>Ask AI</span>
					<span
						className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--brand-magenta)/0.12)]"
						aria-hidden="true"
					>
						<span className="h-2 w-2 rounded-full bg-gradient-unify animate-pulse" />
					</span>
				</button>
			) : null}

			<nav data-tour="sidebar-nav" className="flex-1 space-y-1 px-3">
				{mainNav.map((raw) => {
					let item = raw;
					if (raw.key === 'orders' && ordersInactive) item = { ...raw, inactive: true };
					if (raw.key === 'ads') item = { ...raw, inactive: adsInactive, live: adsLive, paused: adsPaused };
					const Icon = item.icon;
					const badge = item.key === 'orders' ? ordersBadge : null;
					return (
						<NavLink
							key={item.to}
							to={shopPath(item.to)}
							end={item.end}
							className={({ isActive }) =>
								cn(
									'flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors',
									isActive
										? 'bg-sidebar-accent font-semibold text-sidebar-accent-foreground'
										: item.inactive
											? 'text-sidebar-foreground/55 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground'
											: 'text-sidebar-foreground hover:bg-sidebar-accent/60'
								)
							}
						>
							<Icon className="h-[18px] w-[18px]" strokeWidth={2} />
							<span>{item.label}</span>
							{badge ? (
								<span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-[11px] font-semibold text-white">
									{badge}
								</span>
							) : item.inactive ? (
								<span className="ml-auto inline-flex h-5 items-center rounded-full border border-dashed border-primary/40 px-2 text-[10px] font-semibold uppercase tracking-wide text-primary">
									+ Learn more
								</span>
							) : item.paused ? (
								<span
									className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 text-amber-700"
									title="Paused"
									aria-label="Paused"
								>
									<Pause className="h-2.5 w-2.5 fill-current" strokeWidth={0} />
								</span>
							) : item.live ? (
								<span
									className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100"
									title="Live"
									aria-label="Live"
								>
									<span className="h-2 w-2 rounded-full bg-emerald-600" />
								</span>
							) : null}
						</NavLink>
					);
				})}
			</nav>

			<div className="p-3">
				<NavLink
					to={shopPath('/settings')}
					className={({ isActive }) =>
						cn(
							'flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors',
							isActive
								? 'bg-sidebar-accent font-semibold text-sidebar-accent-foreground'
								: 'text-sidebar-foreground hover:bg-sidebar-accent/60'
						)
					}
				>
					<SettingsIcon className="h-[18px] w-[18px]" />
					<span>Settings</span>
				</NavLink>
				<button
					type="button"
					onClick={onSignOut}
					className="mt-1 flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent/60"
				>
					<LogOut className="h-[18px] w-[18px]" />
					<span>Sign out</span>
				</button>
			</div>

			<div className="px-6 pb-2 pt-4">
				<img src={unifyLogo} alt="Unify by NearSt" className="h-20 w-auto" />
			</div>
		</aside>
	);
}
